import { Button } from "@/components/atoms/Button";
import { Label } from "@/components/atoms/Label";
import RichTextEditor from "@/components/organisms/admin/richTextEditor";
import {
	Cross2Icon,
	ExclamationTriangleIcon,
	EyeOpenIcon,
	FileTextIcon,
	MoonIcon,
	Pencil1Icon,
	SunIcon,
} from "@radix-ui/react-icons";
import React, { useMemo, useState } from "react";
import type { BaseFieldProps } from "./types";

// Helper function to calculate content size
const getContentSize = (content: string) => {
	const bytes = new Blob([content]).size;
	return {
		bytes,
		kb: bytes / 1024,
		formatted: bytes < 1024
			? `${bytes} B`
			: `${(bytes / 1024).toFixed(2)} KB`,
	};
};

// Content size limit in KB (server express.json limit is set to 512KB, leaving headroom for other fields + JSON overhead)
const MAX_PAYLOAD_KB = 300;

// Rich Text Editor Popup Component
const RichTextEditorPopup: React.FC<{
	fieldName: string;
	label?: string;
	value: string;
	onChange: (value: string) => void;
	onClose: () => void;
	hasError: boolean;
	validationMessage?: string;
}> = ({
	fieldName,
	label,
	value,
	onChange,
	onClose,
	hasError,
	validationMessage,
}) => {
		const [editorValue, setEditorValue] = useState(value);
		const [sizeError, setSizeError] = useState(false);

		// Calculate content size
		const contentSize = useMemo(() => getContentSize(editorValue), [editorValue]);
		const sizePercentage = (contentSize.kb / MAX_PAYLOAD_KB) * 100;
		const isNearLimit = sizePercentage >= 80;
		const isOverLimit = sizePercentage >= 100;

		const handleSave = () => {
			if (isOverLimit) {
				setSizeError(true);
				return;
			}
			setSizeError(false);
			onChange(editorValue);
			onClose();
		};

		// Clear size error when content changes and is no longer over limit
		const handleEditorChange = (newValue: string) => {
			setEditorValue(newValue);
			if (sizeError && getContentSize(newValue).kb <= MAX_PAYLOAD_KB) {
				setSizeError(false);
			}
		};

		const handleCancel = () => {
			setEditorValue(value); // Reset to original value
			onClose();
		};

		return (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
				<div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl min-w-0 h-[97vh] flex flex-col border border-gray-200 overflow-hidden">
					{/* Header */}
					<div className="flex items-center justify-between py-2 px-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
						<div>
							<h3 className="text-heading-4-semibold font-semibold text-gray-900">
								{label || "Rich Text Editor"}
							</h3>
							{hasError && (
								<p className="text-secondary-main text-body-4-medium mt-1">
									{validationMessage || "This field is required"}
								</p>
							)}
						</div>
						<button
							onClick={onClose}
							className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1.5 transition-all duration-200"
							aria-label="Close editor"
						>
							<Cross2Icon className="w-5 h-5" />
						</button>
					</div>

					{/* Editor Content */}
					<div className="flex-1 min-h-0 min-w-0 p-1 overflow-hidden bg-gray-50 flex flex-col">
						<div className={`flex-1 min-h-0 min-w-0 bg-white border rounded-lg shadow-sm overflow-hidden flex flex-col ${sizeError ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300'
							}`}>
							<RichTextEditor value={editorValue} onChange={handleEditorChange} />
						</div>
					</div>

					{/* Footer */}
					<div className="flex items-center justify-between py-3 px-6 border-t border-gray-200 bg-gray-50">
						{/* Content Size Indicator */}
						<div className="flex items-center gap-3">
							<div className="flex flex-col gap-1">
								<div className="flex items-center gap-2">
									<FileTextIcon className="w-4 h-4 text-gray-500" />
									<span className={`text-sm font-medium ${isOverLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-gray-600'
										}`}>
										{contentSize.formatted} / {MAX_PAYLOAD_KB} KB
									</span>
									{isOverLimit && (
										<span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
											Over Limit
										</span>
									)}
									{isNearLimit && !isOverLimit && (
										<span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
											Near Limit
										</span>
									)}
								</div>
								{/* Progress bar */}
								<div className="w-48 h-1.5 bg-gray-200 rounded-full overflow-hidden">
									<div
										className={`h-full transition-all duration-300 rounded-full ${isOverLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-green-500'
											}`}
										style={{ width: `${Math.min(sizePercentage, 100)}%` }}
									/>
								</div>
								{/* Validation Error Message */}
								{sizeError && (
									<div className="flex items-center gap-1.5 mt-1">
										<ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
										<span className="text-sm text-red-600 font-medium">
											Content exceeds {MAX_PAYLOAD_KB} KB limit. Please reduce the content size to save.
										</span>
									</div>
								)}
							</div>
						</div>

						{/* Action Buttons */}
						<div className="flex items-center gap-3">
							<Button
								type="button"
								onClick={handleCancel}
								classType="admin"
								className="bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 px-6 py-2.5 rounded-lg font-medium transition-all duration-200"
							>
								Cancel
							</Button>
							<Button
								type="button"
								onClick={handleSave}
								disabled={isOverLimit}
								classType="admin"
								className={`px-6 py-2.5 rounded-lg font-medium shadow-sm transition-all duration-200 ${isOverLimit
									? 'bg-gray-300 text-gray-500 cursor-not-allowed'
									: 'bg-primary-main text-white hover:bg-primary-main/90'
									}`}
							>
								{isOverLimit ? 'Cannot Save (Over Limit)' : 'Save Changes'}
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	};

// Rich Text Preview Popup Component
const RichTextPreviewPopup: React.FC<{
	value: string;
	onClose: () => void;
}> = ({ value, onClose }) => {
	const [isDarkMode, setIsDarkMode] = useState(false);
	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
			<div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl min-w-0 h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
				{/* Header */}
				<div className="flex items-center justify-between px-6 py-2 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
					<h3 className="text-heading-4-semibold font-semibold text-gray-900">
						Content Preview
					</h3>
					<button
						onClick={onClose}
						className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1.5 transition-all duration-200"
						aria-label="Close preview"
					>
						<Cross2Icon className="w-5 h-5" />
					</button>
				</div>

				{/* Preview Content */}
				<div className={`flex-1 min-h-0 min-w-0 p-6 overflow-auto ${isDarkMode ? "bg-gray-800" : "bg-gray-50"}`}>
					<div className={`rounded-lg p-6 shadow-sm border min-h-full min-w-0 max-w-full overflow-x-auto ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
						<div
							className="rich-text-content prose prose-sm max-w-full break-words prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-primary-main prose-strong:text-gray-900 [&_table]:w-full"
							dangerouslySetInnerHTML={{ __html: value }}
						/>
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
					<button
						type="button"
						onClick={() => setIsDarkMode((prev) => !prev)}
						className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium text-sm transition-all duration-200"
						aria-label={isDarkMode ? "Switch to Light Background" : "Switch to Dark Background"}
						title={isDarkMode ? "Switch to Light Background" : "Switch to Dark Background"}
					>
						{isDarkMode ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
						<span>{isDarkMode ? "Light" : "Dark"}</span>
					</button>
					<Button
						type="button"
						onClick={onClose}
						classType="admin"
						className="bg-primary-main text-white hover:bg-primary-main/90 px-6 py-2.5 rounded-lg font-medium shadow-sm transition-all duration-200"
					>
						Close
					</Button>
				</div>
			</div>
		</div>
	);
};

// Main Rich Text Field Component
const RichTextField: React.FC<BaseFieldProps> = ({
	fieldName,
	label,
	description,
	placeholder,
	disabled,
	className,
	required,
	hasError,
	validationMessage,
	formData,
	handleChange,
}) => {
	const [showEditor, setShowEditor] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const currentValue = formData[fieldName] || "";

	return (
		<div key={fieldName} className="flex-1 min-w-80 max-w-md mb-4">
			{label && (
				<Label
					htmlFor={fieldName}
					className="block text-body-3-semibold font-semibold text-black mb-2"
				>
					{label}
					{required && <span className="text-secondary-main ml-1">*</span>}
				</Label>
			)}
			{description && (
				<p className="text-body-4-medium text-gray-600 mb-2 max-w-xl">{description}</p>
			)}

			{/* Buttons */}
			<div className="flex gap-2 mb-2">
				<Button
					type="button"
					onClick={() => setShowEditor(true)}
					disabled={disabled}
					classType="admin"
					className={`flex-1 justify-center ${hasError ? "border-secondary-main" : ""}`}
				>
					<div className="flex items-center gap-2">
						<Pencil1Icon className="w-4 h-4" />
						<span>{currentValue ? "Edit Content" : "Add Content"}</span>
					</div>
				</Button>

				{currentValue && (
					<Button
						type="button"
						onClick={() => setShowPreview(true)}
						disabled={disabled}
						classType="admin"
						className="flex-1 justify-center bg-gray-3 text-gray-7 hover:bg-gray-4"
					>
						<div className="flex items-center gap-2">
							<EyeOpenIcon className="w-4 h-4" />
							<span>Preview</span>
						</div>
					</Button>
				)}
			</div>

			{/* Preview of current content */}
			{currentValue && (
				<div className="mt-2 p-2 bg-gray-1 rounded-md border border-gray-3">
					<p className="text-body-4-medium text-gray-6 mb-1">Content Preview:</p>
					<div className="bg-white p-2 rounded max-h-20 overflow-y-auto border border-gray-2">
						<div
							className="text-sm text-gray-7"
							dangerouslySetInnerHTML={{
								__html:
									currentValue.substring(0, 200) +
									(currentValue.length > 200 ? "..." : ""),
							}}
						/>
					</div>
				</div>
			)}

			{/* Popup Editor */}
			{showEditor && (
				<RichTextEditorPopup
					fieldName={fieldName}
					label={label}
					value={currentValue}
					onChange={(value) => handleChange(fieldName, value)}
					onClose={() => setShowEditor(false)}
					hasError={hasError}
					validationMessage={validationMessage}
				/>
			)}

			{/* Preview Popup */}
			{showPreview && (
				<RichTextPreviewPopup
					value={currentValue}
					onClose={() => setShowPreview(false)}
				/>
			)}

			{hasError && (
				<p className="text-secondary-main text-body-4-medium mt-1">
					{validationMessage || "This field is required"}
				</p>
			)}
		</div>
	);
};

export default RichTextField;


